import {
	PORTAL_APERTURE_HALF_LENGTH,
	PORTAL_SURFACE_HALF_THICKNESS,
	portalNormal,
	portalTangent,
} from './portalMath.js';

const readVec = vector => ({x: vector.get_x(), y: vector.get_y()});
// Circle-vs-corner contacts can report a normal between the two adjacent faces.
// Consider both faces there, then keep the first one with enough flat support.
const PORTAL_HIT_SURFACE_ALIGNMENT = Math.cos(45 * Math.PI / 180) - 0.000001;
const PORTAL_HIT_SURFACE_DISTANCE_TOLERANCE = 0.25;
const PORTAL_SURFACE_ALIGNMENT = Math.cos(10 * Math.PI / 180);
const PORTAL_SURFACE_LINE_TOLERANCE = 0.08;
const PORTAL_SURFACE_JOIN_TOLERANCE = 0.05;
const PORTAL_SURFACE_FIT_TOLERANCE = 0.02;

const dot = (left, right) => left.x * right.x + left.y * right.y;

// Separating-axis test for the finite portal boxes, including rotated portals
// and partial overlaps. Merely touching edges is allowed.
export const portalPlacementsOverlap = (left, right) => {
	const frame = placement => ({
		normal: portalNormal(placement.angle),
		tangent: portalTangent(placement.angle),
		depth: placement.surfaceHalfThickness ?? PORTAL_SURFACE_HALF_THICKNESS,
		aperture: placement.apertureHalfLength ?? PORTAL_APERTURE_HALF_LENGTH,
	});
	const a = frame(left), b = frame(right);
	const delta = {x: right.position.x - left.position.x, y: right.position.y - left.position.y};
	const radius = (box, axis) => box.depth * Math.abs(dot(box.normal, axis)) +
		box.aperture * Math.abs(dot(box.tangent, axis));
	return [a.normal, a.tangent, b.normal, b.tangent].every(axis =>
		Math.abs(dot(delta, axis)) < radius(a, axis) + radius(b, axis) - 0.000001);
};

export const findPortalPlacementOverlap = (Box2D, world, placement, replacing = null) => {
	for(let body = world.GetBodyList();
		Box2D.getPointer(body) !== Box2D.getPointer(Box2D.NULL);
		body = body.GetNext()) {
		const endpoint = body.portalEndpoint;
		if(!body.isPortalSurface || !endpoint || endpoint.destroyed || endpoint === replacing) continue;
		endpoint.updateHostTransform?.();
		if(portalPlacementsOverlap(placement, {
			position: readVec(body.GetPosition()),
			angle: body.GetAngle(),
			apertureHalfLength: endpoint.apertureHalfLength,
			surfaceHalfThickness: endpoint.surfaceHalfThickness,
		})) return endpoint;
	}
	return null;
};

const fixtureSurfaceSegments = (Box2D, fixture, childIndex = null) => {
	if(!fixture || fixture.IsSensor()) return [];
	const body = fixture.GetBody();
	const shape = fixture.GetShape();
	const type = shape.GetType();
	const bodyAngle = body.GetAngle();
	const cos = Math.cos(bodyAngle);
	const sin = Math.sin(bodyAngle);
	const worldPoint = localPoint => readVec(body.GetWorldPoint(localPoint));
	const worldNormal = localNormal => ({
		x: localNormal.get_x() * cos - localNormal.get_y() * sin,
		y: localNormal.get_x() * sin + localNormal.get_y() * cos,
	});
	const segments = [];
	const includeEdge = (edge, normal = null) => {
		const start = worldPoint(edge.get_m_vertex1());
		const end = worldPoint(edge.get_m_vertex2());
		if(Math.hypot(end.x - start.x, end.y - start.y) <= Number.EPSILON) return;
		segments.push({start, end, normal, fixture});
	};

	if(type === Box2D.b2Shape.e_polygon) {
		const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
		const count = polygon.get_m_count();
		for(let index = 0; index < count; index++) {
			const nextIndex = (index + 1) % count;
			const edge = {
				get_m_vertex1: () => polygon.get_m_vertices(index),
				get_m_vertex2: () => polygon.get_m_vertices(nextIndex),
			};
			includeEdge(edge, worldNormal(polygon.get_m_normals(index)));
		}
	} else if(type === Box2D.b2Shape.e_edge) {
		includeEdge(Box2D.castObject(shape, Box2D.b2EdgeShape));
	} else if(type === Box2D.b2Shape.e_chain) {
		const chain = Box2D.castObject(shape, Box2D.b2ChainShape);
		const firstChild = Number.isInteger(childIndex) ? childIndex : 0;
		const childEnd = Number.isInteger(childIndex) ? childIndex + 1 : chain.GetChildCount();
		for(let index = firstChild; index < childEnd; index++) {
			if(index < 0 || index >= chain.GetChildCount()) continue;
			const edge = new Box2D.b2EdgeShape();
			chain.GetChildEdge(edge, index);
			includeEdge(edge);
			Box2D.destroy(edge);
		}
	}

	return segments;
};

const prepareSurfaceSegment = (segment, intendedNormal) => {
	const dx = segment.end.x - segment.start.x;
	const dy = segment.end.y - segment.start.y;
	const length = Math.hypot(dx, dy);
	if(length <= Number.EPSILON) return null;
	let tangent = {x: dx / length, y: dy / length};
	let normal = segment.normal || {x: tangent.y, y: -tangent.x};
	let alignment = dot(normal, intendedNormal);
	// Edge and chain shapes can be two-sided. Choose the side the projectile hit.
	if(!segment.normal && alignment < 0) {
		normal = {x: -normal.x, y: -normal.y};
		alignment = -alignment;
	}
	// Keep a stable tangent convention so interval projections can be merged.
	tangent = portalTangent(Math.atan2(normal.y, normal.x));
	return {...segment, length, tangent, normal, alignment};
};

const pointSegmentDistance = (point, segment) => {
	const dx = segment.end.x - segment.start.x;
	const dy = segment.end.y - segment.start.y;
	const lengthSquared = dx * dx + dy * dy;
	if(!lengthSquared) return Math.hypot(point.x - segment.start.x, point.y - segment.start.y);
	const amount = Math.max(0, Math.min(1,
		((point.x - segment.start.x) * dx + (point.y - segment.start.y) * dy) /
		lengthSquared,
	));
	return Math.hypot(
		point.x - (segment.start.x + dx * amount),
		point.y - (segment.start.y + dy * amount),
	);
};

/**
 * Finds a flat, contiguous part of the impacted body large enough for a portal.
 * The requested centre is clamped along that surface, so shots near an edge are
 * moved just far enough to fit. Curved, angled, gapped, or undersized surfaces
 * return null instead of creating a partly unsupported portal.
 */
export const resolvePortalPlacement = (
	Box2D,
	fixture,
	childIndex,
	impactPosition,
	impactAngle,
	apertureHalfLength,
) => {
	if(!fixture || fixture.IsSensor() || !impactPosition ||
		!Number.isFinite(impactPosition.x) || !Number.isFinite(impactPosition.y) ||
		!Number.isFinite(impactAngle) || !Number.isFinite(apertureHalfLength) ||
		apertureHalfLength <= 0) {
		return null;
	}
	const hostBody = fixture.GetBody();
	if(!hostBody || hostBody.destroyed || hostBody.canHavePortal === false ||
		hostBody.mySprite?.data?.canHavePortal === false) {
		return null;
	}

	const intendedNormal = portalNormal(impactAngle);
	const hitSegments = fixtureSurfaceSegments(Box2D, fixture, childIndex)
		.map(segment => prepareSurfaceSegment(segment, intendedNormal))
		.filter(segment => segment &&
			segment.alignment >= PORTAL_HIT_SURFACE_ALIGNMENT &&
			pointSegmentDistance(impactPosition, segment) <=
				PORTAL_HIT_SURFACE_DISTANCE_TOLERANCE)
		.sort((left, right) =>
			right.alignment - left.alignment ||
			pointSegmentDistance(impactPosition, left) -
				pointSegmentDistance(impactPosition, right));
	for(const hitSurface of hitSegments) {
		const normal = hitSurface.normal;
		const tangent = hitSurface.tangent;
		const surfaceNormalDistance = dot(hitSurface.start, normal);
		const intervals = [];
		for(let candidateFixture = hostBody.GetFixtureList();
			Box2D.getPointer(candidateFixture) !== Box2D.getPointer(Box2D.NULL);
			candidateFixture = candidateFixture.GetNext()) {
			for(const rawSegment of fixtureSurfaceSegments(Box2D, candidateFixture)) {
				const segment = prepareSurfaceSegment(rawSegment, normal);
				if(!segment || segment.alignment < PORTAL_SURFACE_ALIGNMENT) continue;
				const startNormalDistance = dot(segment.start, normal);
				const endNormalDistance = dot(segment.end, normal);
				if(Math.abs(startNormalDistance - surfaceNormalDistance) >
					PORTAL_SURFACE_LINE_TOLERANCE ||
					Math.abs(endNormalDistance - surfaceNormalDistance) >
					PORTAL_SURFACE_LINE_TOLERANCE) {
					continue;
				}
				const start = dot(segment.start, tangent);
				const end = dot(segment.end, tangent);
				intervals.push({
					min: Math.min(start, end),
					max: Math.max(start, end),
					fixtures: new Set([candidateFixture]),
				});
			}
		}
		if(!intervals.length) continue;

		intervals.sort((left, right) => left.min - right.min || left.max - right.max);
		const contiguous = [];
		for(const interval of intervals) {
			const current = contiguous[contiguous.length - 1];
			if(!current || interval.min > current.max + PORTAL_SURFACE_JOIN_TOLERANCE) {
				contiguous.push(interval);
				continue;
			}
			current.max = Math.max(current.max, interval.max);
			for(const supportingFixture of interval.fixtures) {
				current.fixtures.add(supportingFixture);
			}
		}

		const impactTangentDistance = dot(impactPosition, tangent);
		const hitSurfaceStart = dot(hitSurface.start, tangent);
		const hitSurfaceEnd = dot(hitSurface.end, tangent);
		const hitSurfaceMinimum = Math.min(hitSurfaceStart, hitSurfaceEnd);
		const hitSurfaceMaximum = Math.max(hitSurfaceStart, hitSurfaceEnd);
		const surfaceAnchorDistance = Math.max(
			hitSurfaceMinimum,
			Math.min(hitSurfaceMaximum, impactTangentDistance),
		);
		const surface = contiguous.find(interval =>
			surfaceAnchorDistance >= interval.min - PORTAL_SURFACE_JOIN_TOLERANCE &&
			surfaceAnchorDistance <= interval.max + PORTAL_SURFACE_JOIN_TOLERANCE);
		if(!surface || surface.max - surface.min <
			apertureHalfLength * 2 - PORTAL_SURFACE_FIT_TOLERANCE) {
			continue;
		}

		const minimumCentre = surface.min + apertureHalfLength;
		const maximumCentre = surface.max - apertureHalfLength;
		const centreTangentDistance = minimumCentre <= maximumCentre ?
			Math.max(minimumCentre, Math.min(maximumCentre, impactTangentDistance)) :
			(surface.min + surface.max) / 2;
		return {
			position: {
				x: normal.x * surfaceNormalDistance + tangent.x * centreTangentDistance,
				y: normal.y * surfaceNormalDistance + tangent.y * centreTangentDistance,
			},
			angle: Math.atan2(normal.y, normal.x),
			hostBody,
			hostFixture: fixture,
			hostFixtures: [...surface.fixtures],
		};
	}
	return null;
};

const includeProjection = (range, point, portalPosition, normal, tangent) => {
	const dx = point.x - portalPosition.x;
	const dy = point.y - portalPosition.y;
	const normalDistance = dx * normal.x + dy * normal.y;
	const tangentDistance = dx * tangent.x + dy * tangent.y;
	range.normal.min = Math.min(range.normal.min, normalDistance);
	range.normal.max = Math.max(range.normal.max, normalDistance);
	range.tangent.min = Math.min(range.tangent.min, tangentDistance);
	range.tangent.max = Math.max(range.tangent.max, tangentDistance);
};

export const rangesOverlap = (left, right, tolerance = 0) =>
	left.max >= right.min - tolerance && right.max >= left.min - tolerance;

export const pointWithinPortalAperture = (point, endpoint, tolerance = 0.02) => {
	const portalPosition = readVec(endpoint.body.GetPosition());
	const tangent = portalTangent(endpoint.body.GetAngle());
	const tangentDistance =
		(point.x - portalPosition.x) * tangent.x +
		(point.y - portalPosition.y) * tangent.y;
	return Math.abs(tangentDistance) <= endpoint.apertureHalfLength + tolerance;
};

export const classifyPortalEntrySide = (
	normalRange,
	normalVelocity,
	positionTolerance = 0.02,
	velocityTolerance = 0.01,
) => {
	// A fixture wholly on one side of the cross-line has an unambiguous entry
	// face, regardless of small joint-induced velocity changes.
	if(normalRange.min >= -positionTolerance) return 'front';
	if(normalRange.max <= positionTolerance) return 'back';

	// Large or fast fixtures can already straddle the line in the first contact
	// frame. Their motion then tells us which face they crossed from.
	if(normalVelocity < -velocityTolerance) return 'front';
	if(normalVelocity > velocityTolerance) return 'back';

	const midpoint = (normalRange.min + normalRange.max) / 2;
	if(midpoint > positionTolerance) return 'front';
	if(midpoint < -positionTolerance) return 'back';
	return 'ambiguous';
};

/**
 * Projects one fixture onto the portal's normal/depth and tangent/aperture axes.
 * Polygon and edge projections are exact; unusual shapes conservatively fall
 * back to their Box2D AABBs.
 */
export const getFixturePortalProjection = (Box2D, fixture, endpoint) => {
	const portalPosition = readVec(endpoint.body.GetPosition());
	const portalAngle = endpoint.body.GetAngle();
	const normal = portalNormal(portalAngle);
	const tangent = portalTangent(portalAngle);
	const range = {
		normal: {min: Infinity, max: -Infinity},
		tangent: {min: Infinity, max: -Infinity},
	};
	const body = fixture.GetBody();
	const shape = fixture.GetShape();
	const type = shape.GetType();
	const include = point => includeProjection(range, point, portalPosition, normal, tangent);
	const includeLocal = localPoint => include(readVec(body.GetWorldPoint(localPoint)));

	if(type === Box2D.b2Shape.e_circle) {
		const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
		const center = readVec(body.GetWorldPoint(circle.get_m_p()));
		include(center);
		const radius = circle.get_m_radius();
		range.normal.min -= radius;
		range.normal.max += radius;
		range.tangent.min -= radius;
		range.tangent.max += radius;
	} else if(type === Box2D.b2Shape.e_polygon) {
		const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
		for(let index = 0; index < polygon.get_m_count(); index++) {
			includeLocal(polygon.get_m_vertices(index));
		}
	} else if(type === Box2D.b2Shape.e_edge) {
		const edge = Box2D.castObject(shape, Box2D.b2EdgeShape);
		includeLocal(edge.get_m_vertex1());
		includeLocal(edge.get_m_vertex2());
	} else {
		for(let child = 0; child < shape.GetChildCount(); child++) {
			const aabb = fixture.GetAABB(child);
			const lower = readVec(aabb.get_lowerBound());
			const upper = readVec(aabb.get_upperBound());
			include({x: lower.x, y: lower.y});
			include({x: lower.x, y: upper.y});
			include({x: upper.x, y: lower.y});
			include({x: upper.x, y: upper.y});
		}
	}

	return Number.isFinite(range.normal.min) ? range : {
		normal: {min: 0, max: 0},
		tangent: {min: 0, max: 0},
	};
};

export const getFixturePortalEntrySide = (Box2D, fixture, endpoint) => {
	const projection = getFixturePortalProjection(Box2D, fixture, endpoint);
	const normal = portalNormal(endpoint.body.GetAngle());
	const velocity = fixture.GetBody().GetLinearVelocity();
	const normalVelocity = velocity.get_x() * normal.x + velocity.get_y() * normal.y;
	return classifyPortalEntrySide(projection.normal, normalVelocity);
};

export const classifyPortalSensorEntryFace = (
	projection,
	halfDepth,
	halfOpening,
	tolerance = 0.02,
) => {
	const penetrations = [
		{face: 'front', depth: halfDepth - projection.normal.min},
		{face: 'back', depth: projection.normal.max + halfDepth},
		{face: 'cap-positive', depth: halfOpening - projection.tangent.min},
		{face: 'cap-negative', depth: projection.tangent.max + halfOpening},
	].filter(entry => entry.depth >= -tolerance);
	if(!penetrations.length) return 'ambiguous';
	const shallowest = Math.min(...penetrations.map(entry => entry.depth));
	const matchingFaces = penetrations.filter(entry => entry.depth <= shallowest + tolerance);
	return matchingFaces.length === 1 ? matchingFaces[0].face : 'ambiguous';
};

export const getFixturePortalSensorEntryFace = (Box2D, fixture, endpoint) =>
	classifyPortalSensorEntryFace(
		getFixturePortalProjection(Box2D, fixture, endpoint),
		endpoint.surfaceHalfThickness,
		endpoint.apertureHalfLength,
	);

export const getPortalCandidateSensorFixtures = (fixtures, candidate) =>
	[...fixtures].filter(fixture =>
		fixture.GetBody()._portalTransitCandidate === candidate
	);

// Only geometry touched by the finite portal box becomes passable. This keeps
// nearby floors, ceilings, and unrelated obstacles solid.
export const fixtureOverlapsPortalBox = (Box2D, fixture, endpoint, tolerance = 0.02) => {
	const projection = getFixturePortalProjection(Box2D, fixture, endpoint);
	return rangesOverlap(
		projection.normal,
		{min: -endpoint.surfaceHalfThickness, max: endpoint.surfaceHalfThickness},
		tolerance,
	) && rangesOverlap(
		projection.tangent,
		{min: -endpoint.apertureHalfLength, max: endpoint.apertureHalfLength},
		tolerance,
	);
};

// A pre-placed portal has no bullet impact to identify its mounting fixture.
// Infer only geometry that crosses the portal's centre line and depth slab;
// floors touching a cap or nearby props must remain ordinary solid geometry.
export const fixtureCanHostPortal = (Box2D, fixture, endpoint, tolerance = 0.05) => {
	if(!fixture || fixture.IsSensor()) return false;
	const projection = getFixturePortalProjection(Box2D, fixture, endpoint);
	return rangesOverlap(
		projection.normal,
		{min: -endpoint.surfaceHalfThickness, max: endpoint.surfaceHalfThickness},
		tolerance,
	) && projection.tangent.min <= tolerance && projection.tangent.max >= -tolerance;
};

export const endpointUsesHostFixture = (Box2D, endpoint, fixture) => {
	if(!endpoint || !fixture) return false;
	const fixturePointer = Box2D.getPointer(fixture);
	if(endpoint.hostFixtureKeys?.has(fixturePointer)) return true;
	if(endpoint.hostFixture &&
		Box2D.getPointer(endpoint.hostFixture) === fixturePointer) {
		return true;
	}
	if(!endpoint.hostBody ||
		Box2D.getPointer(endpoint.hostBody) !== Box2D.getPointer(fixture.GetBody())) {
		return false;
	}
	// Older/runtime callers may only provide the host body. In that case every
	// fixture on that body is part of the mounting surface.
	return !endpoint.hostFixture && !endpoint.hostFixtureKeys?.size;
};

// At level start there is no sensor-entry history yet. Treat a non-sensor
// fixture that already occupies the front half of the finite portal volume as
// an intentional partial transit. Geometry wholly behind the cross-line stays
// blocked, preserving the portal's one-way behavior.
export const fixtureStartsInsidePortal = (Box2D, fixture, endpoint, tolerance = 0.02) => {
	if(!fixture || fixture.IsSensor()) return false;
	const projection = getFixturePortalProjection(Box2D, fixture, endpoint);
	return projection.normal.max >= -tolerance &&
		projection.normal.min <= endpoint.surfaceHalfThickness + tolerance && rangesOverlap(
		projection.tangent,
		{min: -endpoint.apertureHalfLength, max: endpoint.apertureHalfLength},
		tolerance,
	);
};

const isPortalHostOf = (Box2D, endpoint, body) => {
	const host = endpoint?.hostBody;
	return Boolean(host) && !host.destroyed &&
		Box2D.getPointer(host) === Box2D.getPointer(body);
};

// A portal mounted on a dynamic body sits on that body's surface, so its solid
// cross line and cap edges would block or drag the host every step. The pair
// therefore never collides. Editor portals mount on static geometry, where
// Box2D creates no such contact in the first place.
export const isPortalHostContactPair = (Box2D, bodyA, bodyB) => {
	if(!bodyA || !bodyB) return false;
	return isPortalHostOf(Box2D, bodyA.portalEndpoint, bodyB) ||
		isPortalHostOf(Box2D, bodyB.portalEndpoint, bodyA);
};

// Velocity of a body at a world point: v + w x r. Static bodies yield zero, so
// stationary portals keep the plain velocity mapping.
export const bodyVelocityAtPoint = (body, point) => {
	const velocity = body.GetLinearVelocity();
	const angularVelocity = body.GetAngularVelocity();
	const center = body.GetWorldCenter();
	const rx = point.x - center.get_x();
	const ry = point.y - center.get_y();
	return {
		x: velocity.get_x() - angularVelocity * ry,
		y: velocity.get_y() + angularVelocity * rx,
	};
};

// True when no part of the fixture lies behind the portal's cross-line.
export const fixtureLiesInFrontOfPortal = (Box2D, fixture, endpoint, tolerance = 0.02) => {
	if(!fixture) return false;
	return getFixturePortalProjection(Box2D, fixture, endpoint).normal.min >= -tolerance;
};

// True when no part of the fixture extends in front of the portal plane.
export const fixtureLiesBehindPortal = (Box2D, fixture, endpoint, tolerance = 0.02) => {
	if(!fixture) return false;
	return getFixturePortalProjection(Box2D, fixture, endpoint).normal.max <= tolerance;
};

// A wheel meets the portal rim at the plane, not at the end of the thick
// sensor-depth cap. Keep the cap as the broad-phase candidate, but solve the
// exact circle-to-rim contact so a dipping wheel cannot hit an invisible curb.
export const resolvePortalWheelCapContact = (Box2D, contact, capFixture, wheelFixture) => {
	if(wheelFixture.GetShape().GetType() !== Box2D.b2Shape.e_circle) return;
	const circle = Box2D.castObject(wheelFixture.GetShape(), Box2D.b2CircleShape);
	const edge = Box2D.castObject(capFixture.GetShape(), Box2D.b2EdgeShape);
	const rimY = edge.get_m_vertex1().get_y();
	const centre = capFixture.GetBody().GetLocalPoint(
		wheelFixture.GetBody().GetWorldPoint(circle.get_m_p()),
	);
	const dx = centre.get_x();
	const dy = centre.get_y() - rimY;
	const radius = circle.get_m_radius() + edge.get_m_radius();
	if(dx * dx + dy * dy > radius * radius) {
		contact.SetEnabled(false);
		return;
	}
	const capIsA = Box2D.getPointer(contact.GetFixtureA()) === Box2D.getPointer(capFixture);
	const manifold = contact.GetManifold();
	const point = manifold.get_points(0);
	const rimPoint = capIsA ? manifold.get_localPoint() : point.get_localPoint();
	const wheelPoint = capIsA ? point.get_localPoint() : manifold.get_localPoint();
	rimPoint.Set(0, rimY);
	wheelPoint.Set(circle.get_m_p().get_x(), circle.get_m_p().get_y());
	manifold.get_localNormal().Set(0, 0);
	manifold.set_type(Box2D.b2Manifold.e_circles);
	manifold.set_pointCount(1);
	point.get_id().set_key(0);
	// The original edge manifold can carry a cached impulse with a different
	// normal. Let the rim contact solve its own response.
	point.set_normalImpulse(0);
	point.set_tangentImpulse(0);
};
